import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import api from '../services/api';
import { IND, renkoBricks } from '../utils/indicators';
import OrderConfirm from './OrderConfirm';
import useMyPositions from './useMyPositions';
import { ChartBuyBox, ChartSellBox } from './ChartOrderBox';

// CHART WINDOWS (owner 2026-10-13): click a chart icon on any symbol (Watchlist, open positions, trades) ->
// a floating window you can drag, resize and maximise; several at once. Candles / Line / Renko on ANY
// timeframe ('0' = real time, seconds, minutes, hours, days, weeks), every indicator the app uses (add as
// many as you like, change their values, remove them), big-player marks, option-seller walls, and the live
// LTP with BID / ASK.

const IST = 19800;                       // charts show India time
const TFS = ['0', '1s', '5s', '15s', '30s', '1m', '3m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'];
const LS_KEY = 'chart_setup_v1';
const PHONE = typeof window !== 'undefined' && window.innerWidth < 700;
// phones open on 15-min candles: 3 days fit with clear (5 px+) candles (owner 30 Oct 'chart not visible clear')
const DEFAULT_SETUP = { v2: true, v3: true, v4: true, v5: true, v6: true, v7: true, v8: true, v9: true, tf: PHONE ? '15m' : '5m', type: 'candles', renkoMode: 'best', renkoVal: 1.2, renkoAtr: 1.0,
  inds: [{ id: 1, type: 'ema', params: { period: 9 } }, { id: 2, type: 'ema', params: { period: 21 }, color: '#ffb020' },
    { id: 3, type: 'vwap', params: {} }, { id: 4, type: 'volume', params: { ma: 20 } }],
  showBp: false, showWalls: true };   // 30 Oct: big-player arrows OFF by default (record-only data; they squeezed the candles)
const COLORS = ['#00d9ff', '#ffb020', '#c084fc', '#00ff88', '#ff6b6b', '#ffd700', '#7dd3fc', '#f472b6'];

const loadSetup = () => {
  try {
    const s = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (s && s.inds) {
      // 14 Oct: Renko charts default to the CLEAR-VIEW brick on live (the bot's trading brick showed nothing)
      if (!s.v2) { s.v2 = true; if (s.renkoMode === 'bot') s.renkoMode = 'clear'; if (s.type === 'renko') s.tf = '0'; }
      // 22 Oct: the clear view is now the ATR brick on closes (owner: 'remanage renko brick for clear view')
      if (!s.v3) { s.v3 = true; if (s.renkoMode === 'clear' || s.renkoMode === 'bot') s.renkoMode = 'atr'; if (s.type === 'renko' && s.tf === '0') s.tf = '1m'; }
      // 22 Oct (owner): Renko on live / 1-sec ticks with the AI brick of EACH stock (1.5 x its own 5-min ATR)
      if (!s.v5) { s.v5 = true; if (s.type === 'renko' && !['0', '1s', '5s'].includes(s.tf)) s.tf = '1s'; }   // 23 Oct: Renko opens on 1-sec
      if (!s.v4) { s.v4 = true; if (['atr', 'clear', 'bot'].includes(s.renkoMode)) s.renkoMode = 'ai'; if (s.type === 'renko') s.tf = '1s'; }
      // 27 Oct (owner 'repair the renko chart'): the TESTED brick is the default (5-sec test: index 0.10% + 3rd brick)
      if (!s.v6) { s.v6 = true; if (s.renkoMode === 'ai') s.renkoMode = 'best'; }
      // 30 Oct (owner 'chart not visible clear'): on a phone 1m / 5m candles for several days are too thin -> 15m
      // 30 Oct (owner screenshot APLAPOLLO): BP arrows above + below reserved most of a small chart's height -> off
      if (!s.v8) { s.v8 = true; s.showBp = false; }
      // 30 Oct (owner 'why I see too large brick'): a test value of 1% of price stayed saved for every chart -> back to Best once
      if (!s.v9) { s.v9 = true; if (['pct', 'rs'].includes(s.renkoMode)) { s.renkoMode = 'best'; delete s.renkoRs; delete s.renkoPct; } }
      if (!s.v7) { s.v7 = true; if (PHONE && s.type !== 'renko' && ['1m', '5m'].includes(s.tf)) s.tf = '15m'; }
      return { ...DEFAULT_SETUP, ...s };
    }
  } catch (e) { /* default */ }
  return DEFAULT_SETUP;
};
const saveSetup = (s) => { try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch (e) { /* ignore */ } };
const tfPoll = (tf) => (/^(0|tick|\d+s)$/i.test(tf) ? 2000 : /d|w/i.test(tf) ? 60000 : 8000);
const fmt = (v) => (v == null || !isFinite(v) || +v === 0 ? '—' : Math.abs(v) >= 1000 ? (+v).toFixed(1) : (+v).toFixed(2));
const chartOpts = (w, h, secs) => ({
  width: w, height: h, layout: { background: { color: '#0a0a0f' }, textColor: '#aaa', fontSize: 11 },
  grid: { vertLines: { color: '#16162a' }, horzLines: { color: '#16162a' } },
  rightPriceScale: { borderColor: '#2a2a40' },
  timeScale: { borderColor: '#2a2a40', timeVisible: true, secondsVisible: secs, rightOffset: 4, minBarSpacing: 0.5 },
  crosshair: { mode: CrosshairMode.Normal },
});
// BEST RENKO BRICK (owner 2026-10-27, scratchpad bt_renko_grid.py: 13 brick sizes on real 5-sec prices): indices
// 0.10% of price (NIFTY ~23 pts, ~29 bricks/day) was the only size near break-even (3rd brick); the old 1 x 1-min
// ATR made ~85 bricks/day = noise. Stocks/options had no edge at any size -> a calm 2 x 1-min ATR view.
const bestBrick = (data, last, aiB, tf) => {
  const fy = String(data.fy || data.symbol || '');
  const idx = /-INDEX$/i.test(fy), opt = /\d(CE|PE)$/i.test(fy);
  if (idx && !opt) {
    const b = last * 0.001;
    return { brick: b >= 5 ? Math.round(b) : +b.toFixed(2), idx, opt, label: '0.10% of price - tested best for indices' };
  }
  // EACH STOCK ITS OWN BRICK (owner 29 Oct '0.10 is not good for each and every stock'): 8% of THIS stock's (or
  // option's) own average daily range of the last 5 days -> a calm stock gets a small brick, a wild one a big brick,
  // and every chart shows about 20 bricks a day
  const cl = data.renko?.clear;
  if (cl && +cl.pct > 0 && last > 0) {
    const b = last * +cl.pct / 100;
    return { brick: b >= 5 ? Math.round(b * 10) / 10 : +b.toFixed(2), idx, opt,
      label: `${(+cl.pct).toFixed(2)}% = 8% of its own daily range (${cl.range_pct}%) - about 20 bricks a day` };
  }
  const secs = /^(0|\d+s)$/i.test(tf);
  if (aiB > 0) return { brick: secs ? 2 * aiB : aiB, idx, opt, label: secs ? '2 × 1-min ATR, calm view' : 'ATR of this timeframe' };
  return { brick: last * 0.002, idx, opt, label: '0.20% of price' };
};
const lineData = (bars, vals) => bars.map((b, i) => (vals[i] == null || !isFinite(vals[i]) ? { time: b.time } : { time: b.time, value: vals[i] }));

function ChartPane({ data, setup, height, ai, onBrick, onTs, fixedH }) {
  // one main chart + one small chart per oscillator, time-synced
  const wrap = useRef(null);
  const charts = useRef([]);
  // KEEP THE VIEW (owner 29 Oct 'renko on live shows only today'): the live chart is rebuilt every few seconds and
  // used to jump back to the last bar, so older days could never be looked at. The user's scroll / zoom is kept.
  const view = useRef(null);
  const dataRef = useRef(data); dataRef.current = data;
  const aiRef = useRef(ai); aiRef.current = ai;
  const live = useRef(null);
  const [rebuild, setRebuild] = useState(0);
  const skey = data && data.bars?.length ? `${data.fy || data.symbol}|${data.unit}` : '';
  // FIT ON SCREEN (owner 29 Oct): draw into the space that is REALLY left under the header / AI line / tools
  // (they wrap to 2-3 lines on smaller screens), not 'window height - a fixed 118 px'
  const [boxH, setBoxH] = useState(0);
  useEffect(() => {
    const el = wrap.current; if (!el || !window.ResizeObserver) return undefined;
    const ro = new ResizeObserver(() => setBoxH(h => (Math.abs(h - el.clientHeight) > 4 ? el.clientHeight : h)));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const el = wrap.current;
    const data = dataRef.current;
    if (!el || !data || !data.bars?.length) return undefined;
    el.innerHTML = '';
    charts.current.forEach(c => { try { c.remove(); } catch (e) { /* gone */ } });
    charts.current = [];
    live.current = null;
    const secs = data.unit === 's';
    // the bars of one chart data packet (also used for the in-place live updates below)
    const prep = (data) => {
    let bars = data.bars.map(b => ({ ...b, time: b.time + IST }));
    if (setup.type !== 'renko') {
      // CLEAN CANDLES (owner 30 Oct 'some charts are a single vertical line, some have big boxes'): on live / seconds
      // charts 33-77% of the seconds have NO trade (flat dashes) and the next trade looks like a lone vertical line;
      // thin options do the same on minutes. Skip the no-trade bars and start each candle at the previous close.
      const opt = /\d(CE|PE)$/i.test(String(data.fy || data.symbol || ''));
      const link = secs || opt;
      const out = []; let pc = null;
      for (const b of bars) {
        if (pc !== null && b.high === b.low && b.close === pc && !(+b.volume > 0)) continue;   // nothing traded
        if (link && pc !== null) out.push({ ...b, open: pc, high: Math.max(b.high, pc), low: Math.min(b.low, pc) });
        else out.push(b);
        pc = b.close;
      }
      if (out.length >= 2) bars = out;
    }
    let rkPlan = null;
    if (setup.type === 'renko') {
      const last = bars[bars.length - 1].close;
      // CLEAR VIEW (owner 2026-10-22): brick = k x ATR(14) of THIS chart's candles, built from CLOSES only - the
      // brick follows the timeframe (1m small, 15m bigger) and candle wicks no longer make flip-flop bricks
      const atr14 = (() => {
        const n = Math.min(14, bars.length - 1); if (n < 2) return 0;
        let sum = 0;
        for (let k = bars.length - n; k < bars.length; k++) {
          const b = bars[k], pc = bars[k - 1].close;
          sum += Math.max((b.high ?? b.close) - (b.low ?? b.close), Math.abs((b.high ?? b.close) - pc), Math.abs((b.low ?? b.close) - pc));
        }
        return sum / n;
      })();
      const aiB = data.renko?.ai?.brick ? +data.renko.ai.brick : 0;
      const bb = bestBrick(data, last, aiB, setup.tf);
      const brick = setup.renkoMode === 'best' ? bb.brick
        : setup.renkoMode === 'ai' && aiB > 0 ? aiB
        : setup.renkoMode === 'ai' && atr14 > 0 ? Math.max(last * 0.0002, atr14 * 1.5)
        : setup.renkoMode === 'atr' && atr14 > 0 ? Math.max(last * 0.0002, atr14 * (+setup.renkoAtr || 1))
        : setup.renkoMode === 'rs' && +(setup.renkoRs || setup.renkoVal) > 0 ? +(setup.renkoRs || setup.renkoVal)
        : setup.renkoMode === 'pct' && +(setup.renkoPct || setup.renkoVal) > 0 ? (last * +(setup.renkoPct || setup.renkoVal)) / 100
          : setup.renkoMode === 'clear' && data.renko?.clear?.brick ? +data.renko.clear.brick
            : (data.renko?.brick || (last * (data.renko?.pct || 1.2)) / 100);
      if (onBrick) onBrick({ brick, last });   // shown in the brick-size box (owner 29 Oct: 'change the brick by number')
      bars = renkoBricks(bars, brick, !['atr', 'ai', 'best'].includes(setup.renkoMode));
      if (setup.renkoMode === 'best' && bars.length) {
        // RENKO PLAN (owner 2026-10-27; real 5-sec test of 7 indices, 20 days): buying the 1st brick of a new colour
        // = -2.4%/trade (it flips back 1 time in 3), 2nd -1.2%, 3rd brick in a row -0.2% (66% win) vs random -3.3%.
        // Mark the 3rd brick of each run; stocks get no marks (every brick size = random on 140 stocks).
        let run = 0, d0 = 0;
        bars.forEach(b => {
          const d = b.close > b.open ? 1 : -1;
          run = d === d0 ? run + 1 : 1; d0 = d; b._run = run; b._dir = d;
        });
        rkPlan = { ...bb, run, dir: d0 };
      }
      return { bars, rkPlan, brick };
    }
    return { bars, rkPlan, brick: 0 };
    };
    const rkText = (p) => {
      const up = p.dir > 0, col = up ? 'green' : 'red', side = up ? 'CE' : 'PE';
      const msg = !p.idx || p.opt
        ? `${p.opt ? 'Option' : 'Stock'} Renko: no brick size beat random entries in the 5-sec test (140 stocks) - use it to see the trend, not as a buy signal.`
        : p.run >= 3 ? `${p.run} ${col} bricks in a row → ${side} zone: buy ${side} only if the AI / burst signal also says ${side}. Exit by the bot's stop / lock (exiting on the 1st opposite brick tested worse).`
          : p.run === 2 ? `2nd ${col} brick - wait for one more. Buy zone starts at the 3rd brick in a row.`
            : `1st ${col} brick (a turn) - WAIT: a new colour flips back 1 time in 3 (buying here lost -2.4%/trade). Buy zone starts at the 3rd brick.`;
      return [`Brick Rs.${p.brick.toFixed(2)} (${p.label}) · ${msg}`, p.idx && !p.opt && p.run >= 3 ? (up ? '#00ff88' : '#ff6b6b') : '#ffd700'];
    };
    const P0 = prep(data);
    const bars = P0.bars;
    const { rkPlan, brick } = P0;
    if (setup.type === 'renko') {
      if (bars.length && bars.length < 10) {
        const tip = document.createElement('div'); tip.className = 'cw-tip';
        tip.textContent = `Only ${bars.length} bricks - brick Rs.${brick.toFixed(2)} is too big: pick 'Best' or a smaller brick (−).`;
        el.appendChild(tip);
      }
      if (!bars.length) { el.innerHTML = `<div class="cw-empty">No Renko brick of Rs.${brick.toFixed(2)} yet on this timeframe — try a smaller brick or a longer timeframe.</div>`; return undefined; }
    }
    let rkDiv = null;
    if (rkPlan) {
      const [t, c] = rkText(rkPlan);
      rkDiv = document.createElement('div'); rkDiv.className = 'cw-renko';
      rkDiv.style.color = c; rkDiv.textContent = t;
      el.appendChild(rkDiv);
    }
    const allPanes = setup.inds.filter(i => IND[i.type]?.pane);
    const W = el.clientWidth || 600;
    // share the real height: every pane + its label must fit (no scrolling off the screen)
    // SMALL CHART (owner 30 Oct screenshot: candles were a flat line): under 300 px left, the extra panes (volume / RSI ...)
    // are dropped so the price chart keeps the height; □ maximise brings them back
    const free = fixedH ? fixedH : (boxH || height) - Array.from(el.children).reduce((t, c) => t + c.offsetHeight, 0) - 4;
    const panes = free >= 300 ? allPanes : [];
    const avail = Math.max(150, free - panes.length * 18);
    let subH = panes.length ? Math.max(40, Math.min(110, Math.round(avail * 0.2))) : 0;
    if (panes.length && avail - panes.length * subH < avail * 0.45) subH = Math.max(32, Math.floor(avail * 0.55 / panes.length));
    const mainH = Math.max(150, avail - panes.length * subH);
    const mdiv = document.createElement('div'); el.appendChild(mdiv);
    const main = createChart(mdiv, chartOpts(W, mainH, secs));
    charts.current.push(main);
    const ps = setup.type === 'line'
      ? main.addLineSeries({ color: '#00d9ff', lineWidth: 2 })
      : main.addCandlestickSeries({ upColor: '#00ff88', downColor: '#ff4444', borderVisible: false, wickUpColor: '#00ff88', wickDownColor: '#ff4444' });
    const psData = (bs) => (setup.type === 'line' ? bs.map(b => ({ time: b.time, value: b.close })) : bs);
    ps.setData(psData(bars));
    const refresh = [];   // (bars) => re-set one indicator's series: the live update path
    setup.inds.filter(i => !IND[i.type]?.pane).forEach((ind, k) => {
      const d = IND[ind.type]; const col = ind.color || d.color || COLORS[k % COLORS.length];
      const mk = (c, w = 1, style = LineStyle.Solid) => main.addLineSeries({ color: c, lineWidth: w, lineStyle: style, priceLineVisible: false, lastValueVisible: false });
      const ss = ind.type === 'bb' ? [[mk(col, 1, LineStyle.Dashed), 'upper'], [mk(col), 'mid'], [mk(col, 1, LineStyle.Dashed), 'lower']] : [[mk(col, 2), 'main']];
      const set = (bs) => { const r = d.calc(bs, { ...d.params, ...ind.params }); ss.forEach(([s, f]) => s.setData(lineData(bs, r[f]))); };
      set(bars); refresh.push(set);
    });
    const setMarkers = (bars, rkPlan) => {
      // markers: big-player footprints + AI BUY CE / BUY PE / EXIT + the bot's real trades (owner 2026-10-16)
      const mk = [];
      const snap = (t) => { let best = null; for (const b of bars) { if (b.time <= t) best = b.time; else break; } return best; };
      if (setup.showBp && data.big_player?.length) {
        data.big_player.forEach(m => mk.push({ time: m.time + IST, position: m.d > 0 ? 'belowBar' : 'aboveBar',
          color: m.d > 0 ? '#ffd700' : '#c084fc', shape: m.d > 0 ? 'arrowUp' : 'arrowDown', text: 'BP' }));
      }
      const ai = aiRef.current;
      if (setup.showAi !== false && ai) {
        (ai.marks || []).forEach(m => mk.push({ time: snap(m.time + IST), position: m.kind === 'pe' ? 'aboveBar' : m.kind === 'ce' ? 'belowBar' : 'aboveBar',
          color: m.kind === 'ce' ? '#00ff88' : m.kind === 'pe' ? '#ff4444' : '#ffd700',
          shape: m.kind === 'ce' ? 'arrowUp' : m.kind === 'pe' ? 'arrowDown' : 'circle', text: `AI ${m.txt}` }));
        (ai.trades || []).forEach(t => {
          mk.push({ time: snap(t.entry_time + IST), position: 'belowBar', color: '#00d9ff', shape: 'square', text: `BOT IN ${t.contract.slice(-7)} @₹${t.entry}` });
          if (t.exit_time) mk.push({ time: snap(t.exit_time + IST), position: 'aboveBar', color: t.pnl_pct >= 0 ? '#00ff88' : '#ff6b6b', shape: 'square', text: `BOT OUT @₹${t.exit} ${t.pnl_pct >= 0 ? '+' : ''}${t.pnl_pct}%` });
        });
      }
      if (rkPlan && rkPlan.idx && !rkPlan.opt) {
        bars.forEach(b => { if (b._run === 3) mk.push({ time: b.time, position: b._dir > 0 ? 'belowBar' : 'aboveBar', rk: true,
          color: b._dir > 0 ? '#00ff88' : '#ff4444', shape: b._dir > 0 ? 'arrowUp' : 'arrowDown', text: b._dir > 0 ? '3▲ CE ok' : '3▼ PE ok' }); });
      }
      // PHONE / NARROW CHART (owner 2026-10-22: "on mobile signals overlap"): short labels, and on each side (above /
      // below the bars) keep only one marker per few candles - the bot's real trades first, then AI, then BP.
      // markers reserve space above / below the bars: on a phone OR a short chart use short labels and fewer marks
      const narrow = W < 640 || mainH < 300;
      const idx = new Map(bars.map((b, i) => [b.time, i]));
      const gap = narrow ? 8 : 3;
      const rank = (m) => (m.shape === 'square' || m.rk ? 0 : m.text.startsWith('AI') ? 1 : 2);
      const short = (t) => t.replace(/^BOT IN \S+ @/, 'IN ').replace(/^BOT OUT @₹[\d.,]+ /, 'OUT ').replace(/^AI (BUY |EXIT |PICK )?(CE|PE) @([\d,.]+).*/, (x, a1, a2, a3) => (a1 && a1.trim() === 'EXIT' ? 'X ' : '') + a2 + ' ' + a3.split('.')[0]).replace(/^BP$/, '');
      const kept = { aboveBar: [], belowBar: [] };
      mk.filter(m => m.time != null && idx.has(m.time)).sort((x, y) => rank(x) - rank(y) || y.time - x.time).forEach(m => {
        const i = idx.get(m.time); const side = kept[m.position];
        if (side.some(j => Math.abs(j - i) < gap)) return;
        side.push(i); m._i = i;
        if (narrow) m.text = short(m.text);
      });
      const ok = mk.filter(m => m._i != null).sort((x, y) => x.time - y.time);
      ps.setMarkers(ok);
    };
    setMarkers(bars, rkPlan);
    if (setup.showWalls && data.walls) {
      if (data.walls.call) ps.createPriceLine({ price: +data.walls.call, color: '#ff6b6b', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'CALL WALL' });
      if (data.walls.put) ps.createPriceLine({ price: +data.walls.put, color: '#00ff88', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'PUT WALL' });
    }
    panes.forEach((ind) => {
      const d = IND[ind.type]; const r = d.calc(bars, { ...d.params, ...ind.params }); const col = ind.color || d.color;
      const lab = document.createElement('div'); lab.className = 'cw-pane-label';
      lab.textContent = `${d.label} ${Object.values({ ...d.params, ...ind.params }).join(', ')}`;
      if (ind.type === 'volume' && data.volume_src) lab.textContent += ` — from ${data.volume_src} (index futures)`;
      if (ind.type === 'volume' && !bars.some(b => (+b.volume || 0) > 0)) {
        lab.textContent += secs || data.unit === 'tick' || setup.tf === '0'
          ? ' — no volume in live / seconds candles (the live feed carries prices only): pick 1m or higher'
          : ' — no volume for this symbol (indices have no traded volume)';
      }
      el.appendChild(lab);
      const sdiv = document.createElement('div'); el.appendChild(sdiv);
      const sc = createChart(sdiv, { ...chartOpts(W, subH, secs), timeScale: { visible: false } });
      charts.current.push(sc);
      const sets = [];   // (bars, calc result) => setData of one series of this pane
      const ln = (f, c, w = 1) => { const s = sc.addLineSeries({ color: c, lineWidth: w, priceLineVisible: false, lastValueVisible: true }); sets.push((bs, rr) => s.setData(lineData(bs, rr[f]))); };
      if (ind.type === 'macd') {
        const h = sc.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
        sets.push((bs, rr) => h.setData(bs.map((b, i) => ({ time: b.time, value: rr.hist[i] || 0, color: (rr.hist[i] || 0) >= 0 ? '#00ff8888' : '#ff444488' }))));
        ln('main', '#00d9ff'); ln('signal', '#ff8844');
      } else if (ind.type === 'volume') {
        const h = sc.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
        sets.push((bs, rr) => h.setData(bs.map((b, i) => ({ time: b.time, value: rr.vol[i], color: b.close >= b.open ? '#00ff8866' : '#ff444466' }))));
        ln('ma', '#ffb020');
      } else {
        const s = sc.addLineSeries({ color: col, lineWidth: 2, priceLineVisible: false });
        sets.push((bs, rr) => s.setData(lineData(bs, rr.main)));
        if (r.signal) ln('signal', '#ff8844');
        if (r.plus) { ln('plus', '#00ff8899'); ln('minus', '#ff444499'); }
        (r.levels || []).forEach(lv => s.createPriceLine({ price: lv, color: '#555', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false }));
      }
      const set = (bs) => { const rr = d.calc(bs, { ...d.params, ...ind.params }); sets.forEach(f => f(bs, rr)); };
      set(bars); refresh.push(set);
    });
    // keep every pane on the same time window
    const sync = (src) => (range) => { if (!range) return; charts.current.forEach(c => { if (c !== src) c.timeScale().setVisibleLogicalRange(range); }); };
    charts.current.forEach(c => c.timeScale().subscribeVisibleLogicalRangeChange(sync(c)));
    const vkey = `${data.fy || data.symbol}|${setup.type}|${setup.tf}|${setup.renkoMode}`;
    const v = view.current, n = bars.length;
    if (v && v.key === vkey) {
      // same chart refreshed: keep the zoom; stay on the latest bar only if the user was already there
      const w = v.to - v.from;
      main.timeScale().setVisibleLogicalRange(v.atEnd ? { from: n - 1 + 4 - w, to: n - 1 + 4 } : { from: v.from + (n - v.n), to: v.to + (n - v.n) });
    } else if (setup.type === 'renko') {
      // first look at a Renko: several days of bricks, not only today's (~30-80 bricks a day)
      main.timeScale().setVisibleLogicalRange({ from: Math.max(0, n - Math.round(W / 9)), to: n - 1 + 4 });
    } else {
      // ZOOMED IN (owner 30 Oct 'zoom so the movement shows clearly'): open on big candles (~9 px each) at the latest
      // bar; the older days (3+ loaded) are one swipe / the − zoom button away
      main.timeScale().setVisibleLogicalRange({ from: Math.max(0, n - Math.round(W / 9)), to: n - 1 + 4 });
    }
    if (onTs) onTs(main.timeScale());
    // REAL-TIME, NO REDRAW (owner 30 Oct: 'charts fluctuate, I can't press Accept / Reject'): the chart was destroyed and
    // drawn again on every poll (2-8 s) -> a blank frame and everything under it jumped. Now a new data packet only
    // re-sets the series of this chart; false = the shape changed (e.g. Renko plan appears) -> full rebuild.
    live.current = {
      skey,
      update: (nd) => {
        const P = prep(nd);
        if (!P.bars.length || (setup.type === 'renko' && P.bars.length < 10) || !!P.rkPlan !== !!rkDiv) return false;
        ps.setData(psData(P.bars));
        refresh.forEach(f => f(P.bars));
        setMarkers(P.bars, P.rkPlan);
        if (rkDiv && P.rkPlan) { const [t, c] = rkText(P.rkPlan); rkDiv.textContent = t; rkDiv.style.color = c; }
        return true;
      },
      marks: () => { const P = prep(dataRef.current); setMarkers(P.bars, P.rkPlan); },
    };
    return () => {
      live.current = null;
      try {
        const r = main.timeScale().getVisibleLogicalRange();
        if (r) view.current = { key: vkey, from: r.from, to: r.to, n, atEnd: r.to >= n - 2 };
      } catch (e) { /* gone */ }
      charts.current.forEach(c => { try { c.remove(); } catch (e) { /* gone */ } }); charts.current = [];
    };
  }, [skey, setup, height, boxH, fixedH, rebuild]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {    // new bars: update in place (no redraw)
    const lv = live.current;
    if (!lv || !data || !data.bars?.length || lv.skey !== skey) return;
    try { if (!lv.update(data)) setRebuild(x => x + 1); } catch (e) { setRebuild(x => x + 1); }
  }, [data]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { try { if (live.current) live.current.marks(); } catch (e) { /* next update */ } }, [ai]);
  return <div ref={wrap} className="cw-charts" style={fixedH ? { flex: 'none', overflow: 'visible' } : undefined} />;
}

function IndicatorEditor({ setup, onChange }) {
  const [addType, setAddType] = useState('sma');
  const upd = (id, patch) => onChange({ ...setup, inds: setup.inds.map(i => (i.id === id ? { ...i, ...patch } : i)) });
  return (
    <div className="cw-inds">
      {setup.inds.map(ind => {
        const d = IND[ind.type]; if (!d) return null;
        const params = { ...d.params, ...ind.params };
        return (
          <div className="cw-ind" key={ind.id}>
            <input type="color" value={ind.color || d.color} onChange={e => upd(ind.id, { color: e.target.value })} title="colour" />
            <b>{d.label}</b>
            {Object.entries(params).map(([k, v]) => (
              <label key={k}>{k}
                <input type="number" step="any" value={v} onChange={e => upd(ind.id, { params: { ...ind.params, [k]: e.target.value === '' ? v : +e.target.value } })} />
              </label>
            ))}
            <button className="cw-x" title="Remove" onClick={() => onChange({ ...setup, inds: setup.inds.filter(i => i.id !== ind.id) })}>✕</button>
          </div>
        );
      })}
      <div className="cw-ind">
        <select value={addType} onChange={e => setAddType(e.target.value)}>
          {Object.entries(IND).map(([k, d]) => <option key={k} value={k}>{d.label}{d.pane ? ' (panel)' : ''}</option>)}
        </select>
        <button className="btn btn-start cw-btn" onClick={() => onChange({ ...setup, inds: [...setup.inds, { id: Date.now(), type: addType, params: {} }] })}>+ Add</button>
        <label><input type="checkbox" checked={setup.showBp} onChange={e => onChange({ ...setup, showBp: e.target.checked })} /> big-player marks</label>
        <label><input type="checkbox" checked={setup.showAi !== false} onChange={e => onChange({ ...setup, showAi: e.target.checked })} /> AI buy CE/PE + exit marks</label>
        <label><input type="checkbox" checked={setup.showWalls} onChange={e => onChange({ ...setup, showWalls: e.target.checked })} /> seller walls</label>
      </div>
    </div>
  );
}

// REAL-TIME LAST CANDLE (owner 30 Oct 'chart price and bricks should be real time'): the bars come from history every
// 2-8 s (60 s on days), the live price every second. The live price is put into the forming candle (close / high / low)
// or opens the next candle when its time has come, so the last candle and the Renko bricks (built from closes) move
// with every tick. A price more than 20% away from the last close is ignored (bad quote).
function withLive(data, quote) {
  const bars = data?.bars;
  const ltp = +quote?.ltp;
  if (!bars || bars.length < 2 || !(ltp > 0)) return data;
  const last = bars[bars.length - 1];
  if (!(last.close > 0) || Math.abs(ltp / last.close - 1) > 0.2) return data;
  let step = Infinity;
  for (let i = Math.max(1, bars.length - 6); i < bars.length; i++) { const d = bars[i].time - bars[i - 1].time; if (d > 0 && d < step) step = d; }
  if (!isFinite(step)) return data;
  const now = Date.now() / 1000;
  let nb;
  if (step <= 3600 && now >= last.time + step && now < last.time + step * 3) {
    const t = last.time + Math.floor((now - last.time) / step) * step;           // the next candle has started
    nb = [...bars, { time: t, open: last.close, high: Math.max(ltp, last.close), low: Math.min(ltp, last.close), close: ltp, volume: 0 }];
  } else if (now < last.time + step * 3 || step > 3600) {
    if (ltp === last.close) return data;
    nb = [...bars.slice(0, -1), { ...last, close: ltp, high: Math.max(last.high, ltp), low: Math.min(last.low, ltp) }];
  } else {
    return data;                                                                // market closed / old data: leave it
  }
  return { ...data, bars: nb };
}

function ChartWindow({ win, onClose, onFocus, z, embedded = false, embedH = 560 }) {
  const [setup, setSetupState] = useState(loadSetup);
  const setSetup = (s) => { setSetupState(s); saveSetup(s); };
  const [data, setData] = useState(null);
  const [quote, setQuote] = useState(null);
  const liveData = useMemo(() => withLive(data, quote), [data, quote]);
  const [err, setErr] = useState('');
  const [showInd, setShowInd] = useState(false);
  const [customTf, setCustomTf] = useState('');
  // BRICK SIZE BY NUMBER (owner 29 Oct): type Rs. (or % in '% of price' mode) or step it with - / +
  const [brickNow, setBrickNow] = useState(null);   // {brick, last} the chart is drawing with right now
  const [brickTxt, setBrickTxt] = useState('');
  const onBrick = useCallback((b) => setBrickNow(p => (p && p.brick === b.brick && p.last === b.last ? p : b)), []);
  const pctMode = setup.renkoMode === 'pct';
  const brickVal = (v) => {
    const x = Math.max(pctMode ? 0.005 : 0.01, +v);
    const r = x >= 10 ? Math.round(x * 10) / 10 : x >= 1 ? Math.round(x * 100) / 100 : +x.toPrecision(3);
    setSetup(pctMode ? { ...setup, renkoMode: 'pct', renkoPct: r } : { ...setup, renkoMode: 'rs', renkoRs: r });
    setBrickTxt(String(r));
  };
  const brickStep = (f) => {
    const cur = pctMode ? +(setup.renkoPct || setup.renkoVal) : +(brickNow?.brick || setup.renkoRs || 0);
    if (cur > 0) brickVal(cur * f);
  };
  const [max, setMax] = useState(false);
  const [ask, setAsk] = useState(null);       // Buy / Sell from the chart (owner 2026-10-16)
  const [box2, setBox2] = useState(null);     // 'buy' (AI picks CE/PE) | 'sell' (your positions here)
  const [omsg, setOmsg] = useState(null);
  const [ai, setAi] = useState(null);         // AI BUY CE / PE / EXIT marks + AI read now
  const [pos, setPos] = useState({ x: win.x, y: win.y });
  const [size, setSize] = useState(() => {   // SMALL by default (owner 30 Oct 'it should be small'); □ maximises
    const vw = window.innerWidth, vh = window.innerHeight, phone = vw < 700;
    return { w: phone ? Math.round(vw * 0.94) : Math.min(560, vw - 16), h: Math.round(Math.min(phone ? 340 : 400, vh * (phone ? 0.45 : 0.55))) };   // 30 Oct: smaller again
  });
  const tsRef = useRef(null);   // the chart's time scale, for the zoom buttons
  const onTs = useCallback((ts) => { tsRef.current = ts; }, []);
  const zoom = (f) => {          // f < 1 zooms in (bigger candles), f > 1 out (more days); keeps the right edge
    const ts = tsRef.current; if (!ts) return;
    try { const r = ts.getVisibleLogicalRange(); if (!r) return; const w = Math.max(10, (r.to - r.from) * f); ts.setVisibleLogicalRange({ from: r.to - w, to: r.to }); } catch (e) { /* chart gone */ }
  };
  const [infoOpen, setInfoOpen] = useState(false);   // long AI / Renko lines: one line, tap to open
  const size0 = useRef(size);   // the small first size (restore after maximise uses the user's own size)
  const box = useRef(null);
  const drag = useRef(null);
  const fails = useRef(0);
  const [symbol, setSymbol] = useState(win.symbol);
  const my = useMyPositions();

  const load = useCallback(async () => {
    try {
      const r = await api.getChart(symbol, setup.tf, win.exchange, setup.type === 'renko' ? 1 : 0);
      if (r.status === 'success') { setData(r); setErr(''); fails.current = 0; }
      else if (++fails.current >= 3 || !data) setErr(r.message || 'No chart data');
    } catch (e) { if (++fails.current >= 3) setErr('Chart server not reachable'); }
  }, [symbol, setup.tf, setup.type, win.exchange]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); const iv = setInterval(load, tfPoll(setup.tf)); return () => clearInterval(iv); }, [load, setup.tf]);
  useEffect(() => {
    let alive = true;
    const q = async () => { try { const r = await api.getQuote(symbol, win.exchange); if (alive && r.status === 'success') setQuote(r); } catch (e) { /* keep last */ } };
    q(); const iv = setInterval(q, 1000); return () => { alive = false; clearInterval(iv); };   // live price every second
  }, [symbol, win.exchange]);
  useEffect(() => {
    let alive = true;
    const q = async () => { try { const r = await api.getChartAi(symbol); if (alive && r.status === 'success') setAi(r); } catch (e) { /* keep last */ } };
    q(); const iv = setInterval(q, 60000); return () => { alive = false; clearInterval(iv); };
  }, [symbol]);
  const saved = useRef(null);
  useEffect(() => {
    const el = box.current; if (!el || embedded) return;
    if (max) {
      saved.current = { w: el.style.width, h: el.style.height };
      el.style.width = `${window.innerWidth}px`; el.style.height = `${window.innerHeight}px`;   // px: phone 100vh hides under the browser bar
    } else {
      el.style.width = saved.current?.w || `${size0.current.w}px`;
      el.style.height = saved.current?.h || `${size0.current.h}px`;
    }
  }, [max]);
  useEffect(() => {    // follow the window size (CSS resize handle / maximise)
    const el = box.current; if (!el || !window.ResizeObserver) return undefined;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el); return () => ro.disconnect();
  }, []);

  const onDown = (e) => { onFocus(); if (max || embedded) return; drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => {
    const d = drag.current; if (!d) return; const el = box.current;
    const w = el ? el.offsetWidth : 120, h = el ? el.offsetHeight : 40;
    setPos({ x: Math.max(0, Math.min(window.innerWidth - w, d.ox + e.clientX - d.sx)), y: Math.max(0, Math.min(window.innerHeight - h, d.oy + e.clientY - d.sy)) });
  };
  useEffect(() => {    // FIT ON SCREEN (owner 29 Oct): never open / stay partly outside the screen (also on rotate / resize)
    if (embedded) return undefined;
    const fit = () => {
      const el = box.current; if (!el || max) return;
      const vw = window.innerWidth, vh = window.innerHeight;
      if (el.offsetWidth > vw) el.style.width = `${vw}px`;
      if (el.offsetHeight > vh) el.style.height = `${vh}px`;
      setPos(p => ({ x: Math.max(0, Math.min(p.x, vw - el.offsetWidth)), y: Math.max(0, Math.min(p.y, vh - el.offsetHeight)) }));
    };
    const t = setTimeout(fit, 50);
    window.addEventListener('resize', fit);
    return () => { clearTimeout(t); window.removeEventListener('resize', fit); };
  }, [embedded, max]);
  const onUp = () => { drag.current = null; };

  const chg = quote?.chp;
  // size is set on the element itself (not in React's style) so a refresh never undoes the user's resize
  const style = embedded ? { position: 'relative', left: 0, top: 0, width: '100%', height: 'auto', zIndex: 1, resize: 'none', boxShadow: 'none' }
    : max ? { left: 0, top: 0, zIndex: z, resize: 'none' } : { left: pos.x, top: pos.y, zIndex: z };
  const headH = 118 + (showInd ? 0 : 0);
  return (
    <div ref={box} className="cw" style={style} onPointerDown={onFocus}>
      {ask && <OrderConfirm ask={ask} onClose={() => setAsk(null)}
        onResult={(t, m) => { setOmsg({ t, m }); setTimeout(() => setOmsg(null), 8000); }} />}
      {box2 === 'buy' && <ChartBuyBox symbol={symbol} onClose={() => setBox2(null)}
        onResult={(t, m) => { setOmsg({ t, m }); setTimeout(() => setOmsg(null), 8000); }} />}
      {box2 === 'sell' && <ChartSellBox held={my.heldOn(symbol)} onClose={() => setBox2(null)}
        onResult={(t, m) => { setOmsg({ t, m }); setTimeout(() => setOmsg(null), 8000); }} />}
      <div className="cw-head" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}>
        <b>{symbol}</b>
        {data?.underlying && symbol !== data.underlying && (
          <button className="cw-link" onPointerDown={e => e.stopPropagation()} onClick={() => setSymbol(data.underlying)}>underlying {data.underlying}</button>
        )}
        <span className="cw-ltp">{fmt(quote?.ltp)}</span>
        {chg != null && <span style={{ color: chg >= 0 ? '#00ff88' : '#ff4444' }}>{chg >= 0 ? '+' : ''}{(+chg).toFixed(2)}%</span>}
        <span className="cw-ba">BID <b style={{ color: '#00ff88' }}>{fmt(quote?.bid)}</b>{quote?.bid_qty ? ` ×${quote.bid_qty}` : ''} · ASK <b style={{ color: '#ff6b6b' }}>{fmt(quote?.ask)}</b>{quote?.ask_qty ? ` ×${quote.ask_qty}` : ''}</span>
        <span className="cw-sp" />
        {omsg && <span style={{ color: omsg.t === 'success' ? '#00ff88' : '#ff6b6b', fontSize: '0.72rem', maxWidth: '220px' }}>{omsg.m}</span>}
        {/* ONLY Buy and Sell (owner 2026-10-16). Buy on an option = that contract; on a stock / index = the AI picks CE/PE */}
        <button className="cw-hbtn" style={{ background: '#00c46a', color: '#fff', width: 'auto', padding: '0 0.7rem' }} onPointerDown={e => e.stopPropagation()}
          onClick={() => (/\d(CE|PE)$/i.test(symbol) || String(win.exchange || '').toUpperCase() === 'MCX'
            ? setAsk({ symbol, exchange: win.exchange || 'NSE', side: 'BUY', qty: 1, lotSize: 1, label: symbol, price: quote?.ltp ? +(+quote.ltp).toFixed(2) : undefined }) : setBox2('buy'))}>Buy</button>
        {my.heldOn(symbol).length > 0 && <button className="cw-hbtn" style={{ background: '#ff4444', color: '#fff', width: 'auto', padding: '0 0.7rem' }}
          onPointerDown={e => e.stopPropagation()} onClick={() => setBox2('sell')}>Sell</button>}
        {!embedded && <button className="cw-hbtn" onPointerDown={e => e.stopPropagation()} onClick={() => setMax(m => !m)} title={max ? 'Restore' : 'Maximise'}>{max ? '❐' : '□'}</button>}
        {!embedded && <button className="cw-hbtn" onPointerDown={e => e.stopPropagation()} onClick={onClose} title="Close">✕</button>}
      </div>
      {ai?.now && setup.showAi !== false && (
        <div className={`cw-ai ${infoOpen ? 'open' : ''}`} onClick={() => setInfoOpen(o => !o)} title="tap to show / hide the details">
          <b style={{ color: /BUY CE/.test(ai.now.action) ? '#00ff88' : /BUY PE/.test(ai.now.action) ? '#ff6b6b' : '#ffb020' }}>AI {ai.symbol}: {ai.now.action}</b>
          {' — '}{(ai.now.reasons || []).slice(0, 3).join(' · ')}
          <span style={{ color: '#777' }}> | exit: {ai.now.exit_rule}</span>
          {ai.now.value && (
            <div style={{ marginTop: 2 }}>
              <b style={{ color: '#00ff88' }}>Buy {ai.now.value.contract} @ ₹{ai.now.value.premium}</b>
              <span style={{ color: '#aaa' }}> (1 lot = {ai.now.value.lot_size} = ₹{(+ai.now.value.cost_1lot).toLocaleString('en-IN')})</span>
              {' · '}<b style={{ color: '#ff6b6b' }}>stop ₹{ai.now.value.stop}</b> ({ai.now.value.stop_pct}%)
              {' · '}<span style={{ color: '#ffd700' }}>sell / lock: {ai.now.value.locks.map(l => `from ₹${l.from} keep ${l.keep_pct}%`).join(', ')}</span>
            </div>
          )}
        </div>
      )}
      <div className="cw-tools">
        <button className="cw-tf cw-zoom" title="Zoom in - bigger candles" onClick={() => zoom(0.6)}>🔍+</button>
        <button className="cw-tf cw-zoom" title="Zoom out - more days" onClick={() => zoom(1.6)}>🔍−</button>
        {TFS.map(t => <button key={t} className={`cw-tf ${setup.tf === t ? 'on' : ''}`} onClick={() => setSetup({ ...setup, tf: t })}>{t === '0' ? 'live' : t}</button>)}
        <input className="cw-custom" placeholder="any: 0, 45s, 7m, 2h" value={customTf} onChange={e => setCustomTf(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter' && customTf.trim()) setSetup({ ...setup, tf: customTf.trim() }); }} />
        <select value={setup.type} onChange={e => setSetup({ ...setup, type: e.target.value, ...(e.target.value === 'renko' ? { tf: '1s', renkoMode: 'best' } : {}) })}>
          <option value="candles">Candles</option><option value="line">Line</option><option value="renko">Renko</option>
        </select>
        {setup.type === 'renko' && (
          <>
            <select value={setup.renkoMode} onChange={e => { setBrickTxt(''); setSetup({ ...setup, renkoMode: e.target.value }); }}>
              <option value="best">Best: index 0.10% (tested) · stock = own daily range</option>
              <option value="ai">AI brick (per stock){data?.renko?.ai?.brick ? ` (Rs.${(+data.renko.ai.brick).toFixed(2)})` : ''}</option>
              <option value="atr">clear view (ATR × {setup.renkoAtr || 1}, closes)</option>
              <option value="clear">fine (~20 bricks/day){data?.renko?.clear?.brick ? ` (Rs.${(+data.renko.clear.brick).toFixed(2)})` : ''}</option>
              <option value="bot">bot's trading brick{data?.renko?.brick ? ` (Rs.${(+data.renko.brick).toFixed(2)})` : ''}</option>
              <option value="pct">% of price</option><option value="rs">Rs. per brick</option>
            </select>
            {setup.renkoMode === 'atr' && (
              <select value={setup.renkoAtr || 1} onChange={e => setSetup({ ...setup, renkoAtr: +e.target.value })} title="Brick = this x ATR(14) of the chart's candles">
                {[0.5, 0.75, 1, 1.5, 2, 3].map(k => <option key={k} value={k}>{k} × ATR{k === 1 ? ' (clear)' : k < 1 ? ' (detailed)' : ' (calmer)'}</option>)}
              </select>)}
            <span className="cw-brick" title={pctMode ? 'Brick size in % of price' : 'Brick size in Rs. - type any number, or use - / +'}>
              <button className="cw-tf" onClick={() => brickStep(0.8)}>−</button>
              <span className="cw-brick-l">Brick {pctMode ? '%' : '₹'}</span>
              <input className="cw-num" type="number" inputMode="decimal" step="any" min="0"
                     placeholder={pctMode ? String(setup.renkoPct || setup.renkoVal) : brickNow?.brick ? (+brickNow.brick).toFixed(2) : 'Rs.'}
                     value={brickTxt || (setup.renkoMode === 'rs' ? String(setup.renkoRs || '') : pctMode ? String(setup.renkoPct || setup.renkoVal) : '')}
                     onChange={e => { setBrickTxt(e.target.value); const v = parseFloat(e.target.value); if (v > 0) setSetup(pctMode ? { ...setup, renkoPct: v } : { ...setup, renkoMode: 'rs', renkoRs: v }); }}
                     onBlur={() => setBrickTxt('')} />
              <button className="cw-tf" onClick={() => brickStep(1.25)}>+</button>
              {brickNow?.brick > 0 && brickNow.last > 0 && <span className="cw-brick-l">= {(brickNow.brick / brickNow.last * 100).toFixed(2)}%</span>}
            </span>
          </>
        )}
        <button className={`cw-tf ${showInd ? 'on' : ''}`} onClick={() => setShowInd(s => !s)}>Indicators ({setup.inds.length})</button>
      </div>
      {setup.type === 'renko' && data?.renko?.ai?.brick && setup.renkoMode === 'ai' && (
        <div className={`cw-renko ${infoOpen ? 'open' : ''}`} onClick={() => setInfoOpen(o => !o)}>
          AI brick Rs.{(+data.renko.ai.brick).toFixed(2)} = {data.renko.ai.mult} × this symbol's {data.renko.ai.of || '5-min'} ATR (Rs.{data.renko.ai.atr5}) - sized for this timeframe, updated every {data.renko.ai.of === '1-min' ? 'minute' : '5 min'}
          {data.renko.ai.verdict !== undefined && (
            <> · <b style={{ color: data.renko.ai.verdict > 0 ? '#00ff88' : data.renko.ai.verdict < 0 ? '#ff6b6b' : '#aaa' }}>
              Bot's check{data.renko.ai.verdict_of && data.renko.ai.verdict_of !== data.fy ? ` (${String(data.renko.ai.verdict_of).split(':').pop()})` : ''}: last brick {data.renko.ai.verdict > 0 ? '▲ UP → CE allowed, PE blocked' : data.renko.ai.verdict < 0 ? '▼ DOWN → PE allowed, CE blocked' : 'not formed yet → both blocked'}
            </b> <span style={{ color: '#777' }}>(5-min closes)</span></>)}
        </div>
      )}
      {setup.type === 'renko' && setup.renkoMode === 'best' && data?.renko?.ai?.verdict !== undefined && (
        <div className={`cw-renko ${infoOpen ? 'open' : ''}`} onClick={() => setInfoOpen(o => !o)}>
          <b style={{ color: data.renko.ai.verdict > 0 ? '#00ff88' : data.renko.ai.verdict < 0 ? '#ff6b6b' : '#aaa' }}>
            Bot's entry check{data.renko.ai.verdict_of && data.renko.ai.verdict_of !== data.fy ? ` (${String(data.renko.ai.verdict_of).split(':').pop()})` : ''}: last 5-min brick {data.renko.ai.verdict > 0 ? '▲ UP → CE allowed, PE blocked' : data.renko.ai.verdict < 0 ? '▼ DOWN → PE allowed, CE blocked' : 'not formed yet → both blocked'}
          </b>
        </div>
      )}
      {setup.type === 'renko' && data?.renko?.brick && ['bot', 'clear'].includes(setup.renkoMode) && (
        <div className={`cw-renko ${infoOpen ? 'open' : ''}`} onClick={() => setInfoOpen(o => !o)}>
          {setup.renkoMode === 'clear' && data.renko.clear?.brick
            ? <>Chart brick Rs.{(+data.renko.clear.brick).toFixed(2)} ({data.renko.clear.pct}% = clear view, about 20 bricks a day) · </> : null}
          Bot Renko: brick Rs.{(+data.renko.brick).toFixed(2)} ({data.renko.brick_pct}%{data.renko.profile?.range_pct
            ? ` = ${data.renko.profile.mult} × its ${data.renko.profile.range_pct}% daily range` : data.renko.src === 'default' ? ', default' : ''}) ·{' '}
          {data.renko.bricks_today ? <>{data.renko.bricks_today} brick(s) today, last {data.renko.dir > 0 ? '▲ UP' : data.renko.dir < 0 ? '▼ DOWN' : '—'} at {fmt(data.renko.level)}</> : 'no brick yet today'}
        </div>
      )}
      {showInd && <IndicatorEditor setup={setup} onChange={setSetup} />}
      {err && !data ? <div className="cw-empty">{err}</div>
        : !data ? <div className="cw-empty">Loading…</div>
          : <ChartPane data={liveData} setup={setup} ai={ai} onBrick={onBrick} onTs={onTs} fixedH={embedded ? Math.max(220, embedH - 130) : 0} height={Math.max(220, (max ? window.innerHeight : size.h) - headH - (showInd ? 120 : 0))} />}
    </div>
  );
}

// ONE CHART EVERYWHERE (owner 2026-10-16: "not two types of chart"): the Watchlist embeds this same chart.
export function ChartView({ symbol, exchange, height }) {
  const h = height || Math.round(Math.min(440, Math.max(330, window.innerHeight * 0.45)));   // small: under half the screen (owner 30 Oct)
  return <ChartWindow key={`${symbol}|${exchange}`} embedded embedH={h} win={{ symbol, exchange: exchange || 'NSE', x: 0, y: 0 }} z={1}
    onFocus={() => {}} onClose={() => {}} />;
}

export default function ChartHost() {
  // listens for window.dispatchEvent(new CustomEvent('open-chart', { detail: { symbol, exchange } }))
  const [wins, setWins] = useState([]);
  const [top, setTop] = useState(3000);
  useEffect(() => {
    const h = (e) => {
      const d = e.detail || {}; if (!d.symbol) return;
      setWins(w => [...w.slice(-5), { id: Date.now() + Math.random(), symbol: d.symbol, exchange: d.exchange || 'NSE',
        x: Math.max(0, 20 + (w.length % 5) * 28), y: Math.max(0, 70 + (w.length % 5) * 28), z: top + 1 }]);
      setTop(t => t + 1);
    };
    window.addEventListener('open-chart', h);
    return () => window.removeEventListener('open-chart', h);
  }, [top]);
  return wins.map(w => (
    <ChartWindow key={w.id} win={w} z={w.z}
      onFocus={() => { setTop(t => t + 1); setWins(ws => ws.map(x => (x.id === w.id ? { ...x, z: top + 1 } : x))); }}
      onClose={() => setWins(ws => ws.filter(x => x.id !== w.id))} />
  ));
}

export const openChart = (symbol, exchange = 'NSE') =>
  window.dispatchEvent(new CustomEvent('open-chart', { detail: { symbol, exchange } }));
